import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import Loader from "../../components/Loader";
import Pagination from "../../components/Pagination";

import reportService from "../../services/reportService";
import winnerService from "../../services/winnerService";

const PAGE_SIZE = 10;

const money = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const dateTime = (value) =>
  value ? new Date(value).toLocaleString("en-IN") : "-";

const getComputerIdentifier = (row) => {
  if (!row) return "N/A";
  const note = String(row.note || "");

  // 1. Check note for Computer code: e.g. "Computer C1", "Computer 1", "Computer COMP-1"
  const compMatch = note.match(/Computer\s+([A-Za-z0-9_-]+)/i);
  if (compMatch && compMatch[1]) {
    const code = compMatch[1];
    return code.startsWith("💻") ? code : `💻 ${code}`;
  }

  // 2. Check note for Player: e.g. "Player user1"
  const playerMatch = note.match(/Player\s+([A-Za-z0-9_-]+)/i);
  if (playerMatch && playerMatch[1]) {
    return `👤 ${playerMatch[1]}`;
  }

  // 3. Check receiver_username if it contains 💻
  const receiver = String(row.receiver_username || row.receiver || "").trim();
  if (receiver.includes("💻")) {
    return receiver;
  }

  // 4. Check sender_username if it contains 💻
  const sender = String(row.sender_username || row.sender || "").trim();
  if (sender.includes("💻")) {
    return sender;
  }

  // 5. If receiver does not look like a shop name, use it
  if (receiver && !receiver.toLowerCase().includes("shop") && receiver !== "N/A") {
    return `💻 ${receiver}`;
  }

  // 6. If sender does not look like a shop name, use it
  if (sender && !sender.toLowerCase().includes("shop") && sender !== "N/A") {
    return `💻 ${sender}`;
  }

  return "💻 Computer";
};

const TABS = [
  { key: "bets", label: "Computer Bets", mobileLabel: "Bets", icon: "bi-dice-5-fill" },
  { key: "computerPerformance", label: "Computer Performance", mobileLabel: "Performance", icon: "bi-pc-display" },
  { key: "transactions", label: "Computer Transfers", mobileLabel: "Transfers", icon: "bi-arrow-up-right-circle" },
  { key: "takeCoins", label: "Take Coins Report", mobileLabel: "Take Coins", icon: "bi-arrow-down-left-circle" },
  // { key: "winningClaims", label: "Winning Claims", mobileLabel: "Claims", icon: "bi-trophy-fill" },
  // { key: "rounds", label: "Game Rounds", mobileLabel: "Rounds", icon: "bi-controller" },
];

const ShopReports = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("bets");
  const [rows, setRows] = useState([]);
  const [settlement, setSettlement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const loadReport = async () => {
    try {
      setLoading(true);
      setSettlement(null);

      if (activeTab === "settlement") {
        const response = await reportService.getShopSettlement();
        setSettlement(response?.settlement || null);
        setRows([]);
        return;
      }

      if (activeTab === "computerPerformance") {
        const response = await reportService.getShopComputerReport();
        setRows(Array.isArray(response?.computers) ? response.computers : []);
        return;
      }

      if (activeTab === "winningClaims") {
        const response = await winnerService.getPaidWinners();
        setRows(Array.isArray(response?.claims) ? response.claims : []);
        return;
      }

      if (activeTab === "transactions") {
        const response = await reportService.getTransactionReport();
        const transactions = Array.isArray(response?.transactions)
          ? response.transactions
          : [];
        setRows(
          transactions.filter((row) => {
            const tType = String(row.type || "").toUpperCase();
            return tType === "TRANSFER";
          })
        );
        return;
      }

      if (activeTab === "takeCoins") {
        const response = await reportService.getTransactionReport();
        const transactions = Array.isArray(response?.transactions)
          ? response.transactions
          : [];
        setRows(
          transactions.filter((row) => {
            const tType = String(row.type || "").toUpperCase();
            const note = String(row.note || "").toUpperCase();
            return (
              tType === "WITHDRAW" ||
              tType === "TAKE" ||
              tType === "DEDUCT" ||
              note.includes("TAKE") ||
              note.includes("WITHDRAW")
            );
          })
        );
        return;
      }

      if (activeTab === "bets") {
        const response = await reportService.getBetReport();
        setRows(Array.isArray(response?.bets) ? response.bets : []);
        return;
      }

      if (activeTab === "rounds") {
        const response = await reportService.getRoundReport();
        setRows(Array.isArray(response?.rounds) ? response.rounds : []);
        return;
      }

      setRows([]);
    } catch (error) {
      setRows([]);
      setSettlement(null);
      toast.error(
        error.response?.data?.message || "Unable to load report."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSearch("");
    setPage(1);
    loadReport();
  }, [activeTab]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;

    return rows.filter((row) =>
      Object.values(row || {}).some((value) =>
        String(value ?? "").toLowerCase().includes(term)
      )
    );
  }, [rows, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const currentRows = filtered.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE
  );

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const tabTotals = useMemo(() => {
    if (activeTab === "bets") {
      return {
        first: rows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
        second: rows.filter((row) => row.status === "WIN").length,
      };
    }

    if (activeTab === "computerPerformance") {
      return {
        first: rows.reduce((sum, row) => sum + Number(row.current_balance || 0), 0),
        second: rows.reduce((sum, row) => sum + Number(row.net_profit_loss || 0), 0),
      };
    }

    if (activeTab === "transactions" || activeTab === "takeCoins") {
      return {
        first: rows.reduce((sum, row) => sum + Number(row.amount || 0), 0),
        second: rows.length,
      };
    }

    if (activeTab === "winningClaims") {
      return {
        first: rows.reduce(
          (sum, row) => sum + Number(row.win_amount || 0),
          0
        ),
        second: rows.length,
      };
    }

    if (activeTab === "rounds") {
      return {
        first: rows.reduce(
          (sum, row) => sum + Number(row.total_bet_amount || 0),
          0
        ),
        second: rows.filter((row) => row.status === "COMPLETED").length,
      };
    }

    return { first: 0, second: 0 };
  }, [rows, activeTab]);

  const renderTable = () => {
    if (activeTab === "bets") {
      return (
        <>
          {/* DESKTOP TABLE VIEW */}
          <div className="hidden sm:block table-responsive">
            <table className="table admin-table align-middle mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Computer Code / Terminal</th>
                  <th>Round</th>
                  <th>Selection</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Win Amount</th>
                  <th>Claim</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.map((row) => (
                  <tr key={row.id}>
                    <td>#{row.id}</td>
                    <td>
                      <strong>
                        {row.computer_code ? (
                          <span className="text-[#D4AF37]">💻 {row.computer_code}</span>
                        ) : (
                          `@${row.username || "N/A"}`
                        )}
                      </strong>
                    </td>
                    <td>#{row.round_no}</td>
                    <td>
                      <span className="report-selection">{row.bet_type}</span>
                      <small>{row.bet_value}</small>
                    </td>
                    <td>🪙 {money(row.amount)}</td>
                    <td>
                      <span
                        className={`status-pill ${row.status === "WIN"
                          ? "active"
                          : row.status === "LOSE"
                            ? "blocked"
                            : "pending"
                          }`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="fw-bold text-success">
                      🪙 {money(row.win_amount)}
                    </td>
                    <td>
                      {row.status === "WIN" ? (
                        <span
                          className={`status-pill ${row.claim_status === "PAID" ? "active" : "pending"
                            }`}
                        >
                          {row.claim_status || "PENDING"}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      <small>{dateTime(row.created_at)}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* MOBILE CARDS VIEW */}
          <div className="block sm:hidden p-3 space-y-3">
            {currentRows.map((row) => (
              <div
                key={row.id}
                className="rounded-xl border border-[rgba(148,163,184,0.14)] bg-[#0D111C] p-3.5 space-y-2.5"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#F8FAFC]">
                    Bet #{row.id} · Round #{row.round_no}
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${row.status === "WIN"
                      ? "bg-green-500/10 text-green-400 border border-green-500/20"
                      : row.status === "LOSE"
                        ? "bg-red-500/10 text-red-400 border border-red-500/20"
                        : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                      }`}
                  >
                    {row.status}
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-[#D4AF37]">
                    💻 {row.computer_code || `@${row.username || "N/A"}`}
                  </span>
                  <span className="text-[#94A3B8]">
                    {row.bet_type} ({row.bet_value})
                  </span>
                </div>

                <div className="flex items-center justify-between border-t border-[rgba(148,163,184,0.08)] pt-2 text-xs">
                  <div>
                    <span className="text-[#94A3B8]">Wager: </span>
                    <span className="font-bold text-[#F8FAFC]">🪙 {money(row.amount)}</span>
                  </div>
                  <div>
                    <span className="text-[#94A3B8]">Win: </span>
                    <span className="font-bold text-green-400">🪙 {money(row.win_amount)}</span>
                  </div>
                </div>

                <div className="flex items-center justify-between text-[10px] text-[#64748B]">
                  <span>{dateTime(row.created_at)}</span>
                  {row.status === "WIN" && (
                    <span className="font-bold text-[#4ADE80]">
                      Claim: {row.claim_status || "PENDING"}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      );
    }

    if (activeTab === "computerPerformance") {
      return (
        <>
          {/* DESKTOP TABLE VIEW */}
          <div className="hidden sm:block table-responsive">
            <table className="table admin-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Computer Code / ID</th>
                  <th>Status</th>
                  <th>Current Balance</th>
                  <th>Total Wagered</th>
                  <th>Total Won</th>
                  <th>Net Profit / Loss</th>
                  <th>Last Login</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <strong>💻 {row.computer_code}</strong>
                    </td>
                    <td>
                      <span
                        className={`status-pill ${row.status === "ACTIVE" ? "active" : "blocked"
                          }`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="fw-bold">🪙 {money(row.current_balance)}</td>
                    <td>🪙 {money(row.total_wagered)}</td>
                    <td className="text-success">🪙 {money(row.total_won)}</td>
                    <td
                      className={`fw-bold ${Number(row.net_profit_loss) >= 0
                        ? "text-success"
                        : "text-danger"
                        }`}
                    >
                      🪙 {money(row.net_profit_loss)}
                    </td>
                    <td>
                      <small>{dateTime(row.last_login)}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* MOBILE CARDS VIEW */}
          <div className="block sm:hidden p-3 space-y-3">
            {currentRows.map((row) => (
              <div
                key={row.id}
                className="rounded-xl border border-[rgba(148,163,184,0.14)] bg-[#0D111C] p-3.5 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-extrabold text-[#F8FAFC]">
                    💻 {row.computer_code}
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold ${row.status === "ACTIVE"
                      ? "bg-green-500/10 text-green-400 border border-green-500/20"
                      : "bg-red-500/10 text-red-400 border border-red-500/20"
                      }`}
                  >
                    {row.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 rounded-lg bg-[#121827] p-2.5 text-xs">
                  <div>
                    <span className="text-[10px] text-[#94A3B8]">Balance</span>
                    <p className="font-bold text-[#D4AF37]">🪙 {money(row.current_balance)}</p>
                  </div>
                  <div>
                    <span className="text-[10px] text-[#94A3B8]">Net P/L</span>
                    <p
                      className={`font-bold ${Number(row.net_profit_loss) >= 0
                        ? "text-green-400"
                        : "text-red-400"
                        }`}
                    >
                      🪙 {money(row.net_profit_loss)}
                    </p>
                  </div>
                  <div>
                    <span className="text-[10px] text-[#94A3B8]">Total Wagered</span>
                    <p className="font-semibold text-[#F8FAFC]">🪙 {money(row.total_wagered)}</p>
                  </div>
                  <div>
                    <span className="text-[10px] text-[#94A3B8]">Total Won</span>
                    <p className="font-semibold text-green-400">🪙 {money(row.total_won)}</p>
                  </div>
                </div>

                <div className="text-[10px] text-[#64748B]">
                  Last Login: {dateTime(row.last_login)}
                </div>
              </div>
            ))}
          </div>
        </>
      );
    }

    if (activeTab === "winningClaims") {
      return (
        <>
          {/* DESKTOP TABLE VIEW */}
          <div className="hidden sm:block table-responsive">
            <table className="table admin-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Bet ID</th>
                  <th>Computer Code</th>
                  <th>Round</th>
                  <th>Bet</th>
                  <th>Winning No.</th>
                  <th>Winning Amount</th>
                  <th>Wallet Before</th>
                  <th>Wallet After</th>
                  <th>Paid At</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.map((claim) => (
                  <tr key={claim.transaction_id || claim.bet_id}>
                    <td>
                      <strong>#{claim.bet_id}</strong>
                    </td>
                    <td>
                      <strong>
                        {claim.computer_code ? (
                          <span className="text-[#D4AF37]">💻 {claim.computer_code}</span>
                        ) : (
                          `@${claim.username || "N/A"}`
                        )}
                      </strong>
                    </td>
                    <td>#{claim.round_no || "N/A"}</td>
                    <td>
                      <span className="report-selection">
                        {claim.bet_type || "N/A"}
                      </span>
                      <small>{claim.bet_value ?? "N/A"}</small>
                    </td>
                    <td>
                      <span className="round-winning-number">
                        {claim.winning_number ?? "-"}
                      </span>
                    </td>
                    <td className="fw-bold text-success">
                      🪙 {money(claim.win_amount)}
                    </td>
                    <td>🪙 {money(claim.shop_wallet_before)}</td>
                    <td className="fw-bold text-success">
                      🪙 {money(claim.shop_wallet_after)}
                    </td>
                    <td>
                      <small>{dateTime(claim.paid_at)}</small>
                    </td>
                    <td>
                      <span className="status-pill active">PAID</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* MOBILE CARDS VIEW */}
          <div className="block sm:hidden p-3 space-y-3">
            {currentRows.map((claim) => (
              <div
                key={claim.transaction_id || claim.bet_id}
                className="rounded-xl border border-[rgba(148,163,184,0.14)] bg-[#0D111C] p-3.5 space-y-2.5"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#F8FAFC]">
                    Claim #{claim.bet_id} · Round #{claim.round_no || "N/A"}
                  </span>
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold bg-green-500/10 text-green-400 border border-green-500/20">
                    PAID
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-[#D4AF37]">
                    💻 {claim.computer_code || `@${claim.username || "N/A"}`}
                  </span>
                  <span className="text-[#94A3B8]">
                    Win No: <strong className="text-[#F8FAFC]">{claim.winning_number ?? "-"}</strong>
                  </span>
                </div>

                <div className="rounded-lg bg-[#121827] p-2.5 flex items-center justify-between text-xs">
                  <span className="text-[#94A3B8]">Winning Amount:</span>
                  <span className="font-bold text-green-400">🪙 {money(claim.win_amount)}</span>
                </div>

                <div className="flex items-center justify-between text-[10px] text-[#64748B]">
                  <span>Paid At: {dateTime(claim.paid_at)}</span>
                  <span>Wallet After: 🪙 {money(claim.shop_wallet_after)}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      );
    }

    if (activeTab === "transactions") {
      return (
        <>
          {/* DESKTOP TABLE VIEW */}
          <div className="hidden sm:block table-responsive">
            <table className="table admin-table align-middle mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Computer / Receiver</th>
                  <th>Amount Transferred</th>
                  <th>Action</th>
                  <th>Note</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.map((row) => (
                  <tr key={row.id}>
                    <td>#{row.id}</td>
                    <td>
                      <strong>
                        {getComputerIdentifier(row)}
                      </strong>
                    </td>
                    <td className="fw-bold text-success">
                      + 🪙 {money(row.amount)}
                    </td>
                    <td>
                      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold bg-green-500/10 text-green-400 border border-green-500/20">
                        TRANSFER
                      </span>
                    </td>
                    <td>
                      <small>{row.note || "-"}</small>
                    </td>
                    <td>
                      <small>{dateTime(row.created_at)}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* MOBILE CARDS VIEW */}
          <div className="block sm:hidden p-3 space-y-3">
            {currentRows.map((row) => (
              <div
                key={row.id}
                className="rounded-xl border border-[rgba(148,163,184,0.14)] bg-[#0D111C] p-3.5 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#F8FAFC]">
                    Transfer #{row.id}
                  </span>
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold bg-green-500/10 text-green-400 border border-green-500/20">
                    TRANSFER
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#94A3B8]">To Computer:</span>
                  <span className="font-bold text-[#D4AF37]">
                    {getComputerIdentifier(row)}
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#94A3B8]">Amount:</span>
                  <span className="font-bold text-green-400">+ 🪙 {money(row.amount)}</span>
                </div>

                <div className="flex items-center justify-between text-[10px] text-[#64748B] pt-1 border-t border-[rgba(148,163,184,0.08)]">
                  <span>Note: {row.note || "-"}</span>
                  <span>{dateTime(row.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      );
    }

    if (activeTab === "takeCoins") {
      return (
        <>
          {/* DESKTOP TABLE VIEW */}
          <div className="hidden sm:block table-responsive">
            <table className="table admin-table align-middle mb-0">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Computer / Terminal</th>
                  <th>Amount Taken</th>
                  <th>Action</th>
                  <th>Note</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.map((row) => (
                  <tr key={row.id}>
                    <td>#{row.id}</td>
                    <td>
                      <strong>
                        {getComputerIdentifier(row)}
                      </strong>
                    </td>
                    <td className="fw-bold text-danger">
                      - 🪙 {money(row.amount)}
                    </td>
                    <td>
                      <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold bg-red-500/10 text-red-400 border border-red-500/20">
                        TAKE
                      </span>
                    </td>
                    <td>
                      <small>{row.note || "-"}</small>
                    </td>
                    <td>
                      <small>{dateTime(row.created_at)}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* MOBILE CARDS VIEW */}
          <div className="block sm:hidden p-3 space-y-3">
            {currentRows.map((row) => (
              <div
                key={row.id}
                className="rounded-xl border border-[rgba(148,163,184,0.14)] bg-[#0D111C] p-3.5 space-y-2"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#F8FAFC]">
                    Take Coins #{row.id}
                  </span>
                  <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold bg-red-500/10 text-red-400 border border-red-500/20">
                    TAKE
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#94A3B8]">From Computer:</span>
                  <span className="font-bold text-[#D4AF37]">
                    {getComputerIdentifier(row)}
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#94A3B8]">Amount Taken:</span>
                  <span className="font-bold text-red-400">- 🪙 {money(row.amount)}</span>
                </div>

                <div className="flex items-center justify-between text-[10px] text-[#64748B] pt-1 border-t border-[rgba(148,163,184,0.08)]">
                  <span>Note: {row.note || "-"}</span>
                  <span>{dateTime(row.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      );
    }

    if (activeTab === "rounds") {
      return (
        <>
          {/* DESKTOP TABLE VIEW */}
          <div className="hidden sm:block table-responsive">
            <table className="table admin-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Round</th>
                  <th>Winning Number</th>
                  <th>Status</th>
                  <th>Total Bets</th>
                  <th>Bet Amount</th>
                  <th>Win Amount</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {currentRows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <strong>#{row.round_no}</strong>
                      <small>ID #{row.id}</small>
                    </td>
                    <td>
                      <span className="round-winning-number">
                        {row.winning_number ?? "-"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`status-pill ${row.status === "COMPLETED" ? "active" : "pending"
                          }`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td>{row.total_bets || 0}</td>
                    <td>🪙 {money(row.total_bet_amount)}</td>
                    <td className="fw-bold text-success">
                      🪙 {money(row.total_win_amount)}
                    </td>
                    <td>
                      <small>{dateTime(row.created_at)}</small>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* MOBILE CARDS VIEW */}
          <div className="block sm:hidden p-3 space-y-3">
            {currentRows.map((row) => (
              <div
                key={row.id}
                className="rounded-xl border border-[rgba(148,163,184,0.14)] bg-[#0D111C] p-3.5 space-y-2.5"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-[#F8FAFC]">
                    Round #{row.round_no}
                  </span>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[9px] font-bold ${row.status === "COMPLETED"
                      ? "bg-green-500/10 text-green-400 border border-green-500/20"
                      : "bg-amber-500/10 text-amber-400 border border-amber-500/20"
                      }`}
                  >
                    {row.status}
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#94A3B8]">Winning No:</span>
                  <span className="font-bold text-[#D4AF37] text-sm">
                    {row.winning_number ?? "-"}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-2 rounded-lg bg-[#121827] p-2 text-xs">
                  <div>
                    <span className="text-[10px] text-[#94A3B8]">Total Bets</span>
                    <p className="font-bold text-[#F8FAFC]">{row.total_bets || 0}</p>
                  </div>
                  <div>
                    <span className="text-[10px] text-[#94A3B8]">Bet Amount</span>
                    <p className="font-bold text-[#F8FAFC]">🪙 {money(row.total_bet_amount)}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between text-[10px] text-[#64748B]">
                  <span>Win Amount: <strong className="text-green-400">🪙 {money(row.total_win_amount)}</strong></span>
                  <span>{dateTime(row.created_at)}</span>
                </div>
              </div>
            ))}
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div className="d-flex flex-column min-vh-100 app-shell">
      <Navbar
        toggleSidebar={() => setSidebarOpen((value) => !value)}
      />

      <div className="d-flex flex-grow-1 position-relative">
        <Sidebar
          isOpen={sidebarOpen}
          closeMobileSidebar={() => setSidebarOpen(false)}
        />

        <main className="app-main flex-grow-1 overflow-auto">
          <div className="container-fluid admin-page-wrap">
            <div className="admin-page-header">
              <div>
                <span className="admin-page-kicker">SHOP REPORTS</span>
                <h2>Reports</h2>
                <p>
                  Computer betting, transfers, winning claims, rounds and performance.
                </p>
              </div>

              <button
                className="btn btn-outline-primary"
                onClick={loadReport}
                disabled={loading}
              >
                <i className="bi bi-arrow-clockwise me-2" />
                Refresh
              </button>
            </div>

            {/* DESKTOP TABS (SHOWN ON LAPTOP / DESKTOP) */}
            <div className="hidden sm:flex reports-tabs mb-4 flex-wrap gap-2">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  className={activeTab === tab.key ? "active" : ""}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <i className={`bi ${tab.icon}`} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* MOBILE REPORT SELECTOR GRID - NO SCROLLER, NO MODAL, 100% VISIBLE & HIGHLY ATTRACTIVE */}
            <div className="block sm:hidden mb-4">
              <div className="grid grid-cols-3 gap-1.5 p-1.5 rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[#0D111A]/95 shadow-xl backdrop-blur-md">
                {TABS.map((tab) => {
                  const isActive = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setActiveTab(tab.key)}
                      className={`relative flex flex-col items-center justify-center py-2 px-1 rounded-xl border transition-all duration-200 active:scale-95 ${isActive
                        ? "border-[#D4AF37] bg-gradient-to-b from-[#D4AF37]/25 via-[#D4AF37]/10 to-[#121827] text-[#F8FAFC] shadow-lg shadow-[#D4AF37]/25"
                        : "border-transparent bg-[#121827]/60 text-[#94A3B8] hover:bg-[#121827] hover:text-[#F8FAFC]"
                        }`}
                    >
                      {/* Active indicator top glow line */}
                      {isActive && (
                        <span className="absolute -top-[1px] left-2 right-2 h-[2px] rounded-full bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent shadow-[0_0_10px_#D4AF37]" />
                      )}

                      <div
                        className={`flex h-7 w-7 items-center justify-center rounded-lg mb-1 transition-all ${isActive
                          ? "bg-[#D4AF37] text-[#070A12] font-black shadow-md shadow-[#D4AF37]/40 scale-105"
                          : "bg-[#070A12]/80 text-[#D4AF37] border border-[rgba(212,175,55,0.15)]"
                          }`}
                      >
                        <i className={`bi ${tab.icon} text-xs`} />
                      </div>

                      <span
                        className={`text-[10px] font-extrabold tracking-tight text-center leading-tight ${isActive ? "text-[#D4AF37]" : "text-[#94A3B8]"
                          }`}
                      >
                        {tab.mobileLabel}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {activeTab === "settlement" ? (
              loading ? (
                <div className="admin-panel p-5">
                  <Loader message="Loading settlement..." />
                </div>
              ) : (
                <div className="row g-3">
                  {[
                    [
                      "Total Bets",
                      settlement?.total_bets,
                      "bi-dice-5-fill",
                      "tone-primary",
                    ],
                    [
                      "Bet Amount",
                      `🪙 ${money(settlement?.total_bet_amount)}`,
                      "bi-wallet2",
                      "tone-info",
                    ],
                    [
                      "Cash To Pay",
                      `🪙 ${money(settlement?.cash_to_pay)}`,
                      "bi-hourglass-split",
                      "tone-warning",
                    ],
                    [
                      "Pending Claims",
                      settlement?.pending_claims,
                      "bi-clock-history",
                      "tone-warning",
                    ],
                    [
                      "Paid Claims",
                      settlement?.paid_claims,
                      "bi-check-circle-fill",
                      "tone-success",
                    ],
                    [
                      "Winning Coins Received",
                      `🪙 ${money(settlement?.winning_coins_received)}`,
                      "bi-cash-coin",
                      "tone-success",
                    ],
                    [
                      "Total Winning",
                      `🪙 ${money(settlement?.total_winning_amount)}`,
                      "bi-trophy-fill",
                      "tone-primary",
                    ],
                  ].map(([label, value, icon, tone]) => (
                    <div className="col-6 col-xl-3" key={label}>
                      <div className="admin-stat-card h-100">
                        <span className={`admin-stat-icon ${tone}`}>
                          <i className={`bi ${icon}`} />
                        </span>
                        <div>
                          <div className="admin-stat-label">{label}</div>
                          <div className="admin-stat-value admin-stat-value-sm">
                            {value ?? 0}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              <>
                <div className="row g-3 mb-4">
                  <div className="col-6 col-xl-3">
                    <div className="admin-stat-card h-100">
                      <span className="admin-stat-icon tone-primary">
                        <i className="bi bi-list-ul" />
                      </span>
                      <div>
                        <div className="admin-stat-label">Records</div>
                        <div className="admin-stat-value">{rows.length}</div>
                      </div>
                    </div>
                  </div>

                  <div className="col-6 col-xl-3">
                    <div className="admin-stat-card h-100">
                      <span className="admin-stat-icon tone-success">
                        <i className="bi bi-coin" />
                      </span>
                      <div>
                        <div className="admin-stat-label">
                          {activeTab === "rounds"
                            ? "Bet Volume"
                            : activeTab === "bets"
                              ? "Bet Amount"
                              : activeTab === "winningClaims"
                                ? "Claim Amount"
                                : activeTab === "computerPerformance"
                                  ? "Total PC Balance"
                                  : activeTab === "takeCoins"
                                    ? "Coins Taken"
                                    : activeTab === "transactions"
                                      ? "Coins Transferred"
                                      : "Coin Volume"}
                        </div>
                        <div className="admin-stat-value admin-stat-value-sm">
                          🪙 {money(tabTotals.first)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-6 col-xl-3">
                    <div className="admin-stat-card h-100">
                      <span className="admin-stat-icon tone-info">
                        <i className="bi bi-check-circle" />
                      </span>
                      <div>
                        <div className="admin-stat-label">
                          {activeTab === "bets"
                            ? "Wins"
                            : activeTab === "rounds"
                              ? "Completed"
                              : activeTab === "winningClaims"
                                ? "Paid Claims"
                                : activeTab === "computerPerformance"
                                  ? "Total Net P/L"
                                  : "Matched"}
                        </div>
                        <div className="admin-stat-value admin-stat-value-sm">
                          {activeTab === "computerPerformance"
                            ? `🪙 ${money(tabTotals.second)}`
                            : tabTotals.second}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-6 col-xl-3">
                    <div className="admin-stat-card h-100">
                      <span className="admin-stat-icon tone-warning">
                        <i className="bi bi-filter" />
                      </span>
                      <div>
                        <div className="admin-stat-label">
                          Visible Results
                        </div>
                        <div className="admin-stat-value">
                          {filtered.length}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="admin-panel">
                  <div className="admin-panel-head flex-wrap gap-3">
                    <div>
                      <h5>
                        {
                          TABS.find((tab) => tab.key === activeTab)?.label
                        }
                      </h5>
                      <p>Search and review the latest available records.</p>
                    </div>

                    <div className="admin-search-wrap">
                      <i className="bi bi-search" />
                      <input
                        value={search}
                        onChange={(event) => {
                          setSearch(event.target.value);
                          setPage(1);
                        }}
                        placeholder="Search report..."
                      />
                    </div>
                  </div>

                  {loading ? (
                    <div className="p-5">
                      <Loader message="Loading report..." />
                    </div>
                  ) : currentRows.length === 0 ? (
                    <div className="admin-empty-state">
                      <i className="bi bi-inbox" />
                      <strong>No report records found</strong>
                      <span>
                        There is nothing to display for this selection.
                      </span>
                    </div>
                  ) : (
                    <div className="table-responsive">{renderTable()}</div>
                  )}

                  {!loading && filtered.length > PAGE_SIZE && (
                    <div className="admin-pagination-wrap">
                      <span>
                        Showing {(page - 1) * PAGE_SIZE + 1}–
                        {Math.min(page * PAGE_SIZE, filtered.length)} of{" "}
                        {filtered.length}
                      </span>
                      <Pagination
                        currentPage={page}
                        totalPages={totalPages}
                        onPageChange={setPage}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ShopReports;
